/**
 * AppErrorBoundary — a React class-based error boundary used as the
 * outermost wrapper around <RouterProvider/>. Catches synchronous
 * rendering errors that escape the per-route `errorElement` (e.g. a
 * throw inside <AuthProvider/> or <ThemeProvider/>). Renders the
 * same branded fallback so the developer 404 screen is impossible
 * to reach.
 */
import { Component } from "react";
import RouteErrorPage from "@/components/errors/RouteErrorPage";
import { RouterProvider as _MinimalRouter, createMemoryRouter } from "react-router-dom";

// Minimal one-route router to satisfy hooks used by RouteErrorPage
// (useRouteError, useLocation, useNavigate) when it is rendered
// *outside* the primary RouterProvider.
const _fallbackRouter = createMemoryRouter([
  { path: "/", element: <RouteErrorPage /> },
]);

export default class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) {
    try {
      const url = `${(process.env.REACT_APP_BACKEND_URL || "")}/api/logs/frontend-error`;
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob(
          [JSON.stringify({
            ref: `boundary-${Date.now().toString(36)}`,
            status: 500,
            pathname: window.location.pathname,
            message: String(err?.message || err).slice(0, 300),
            component_stack: (info?.componentStack || "").slice(0, 400),
            ts: new Date().toISOString(),
          })], { type: "application/json" }));
      }
    } catch { /* observability must never crash */ }
  }
  render() {
    if (this.state.hasError) {
      return <_MinimalRouter router={_fallbackRouter} />;
    }
    return this.props.children;
  }
}
